import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import AttendanceTracker from './AttendanceTracker';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiSquare, FiClock, FiCalendar, FiList, FiUsers, FiFilter, FiActivity } = FiIcons;

const TimeTracking = () => {
  const { tasks, activeTimer, startTimer, stopTimer, teamMembers } = useData();
  const { user, hasPermission } = useAuth();
  const [selectedTask, setSelectedTask] = useState('');
  const [description, setDescription] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [activeTab, setActiveTab] = useState('attendance');
  const [filterEmployee, setFilterEmployee] = useState('all');

  const myTasks = tasks.filter(t => {
    if (t.status === 'completed') return false;
    let assignees = Array.isArray(t.assignedTo) ? t.assignedTo : [t.assignedTo];
    return assignees.some(id => String(id) === String(user.id));
  });

  useEffect(() => {
    let interval;
    if (activeTimer) {
      interval = setInterval(() => {
        setElapsed(Date.now() - activeTimer.startTime);
      }, 1000);
    } else {
      setElapsed(0);
    }
    return () => clearInterval(interval);
  }, [activeTimer]);

  const formatTime = (ms) => {
    const seconds = Math.floor((ms / 1000) % 60);
    const minutes = Math.floor((ms / 1000 / 60) % 60);
    const hours = Math.floor(ms / 1000 / 60 / 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleStartTimer = () => {
    if (selectedTask) {
      startTimer(selectedTask, description);
      setDescription('');
    }
  };

  const getLogs = (isTeamView) => {
    const logs = [];
    tasks.forEach(task => {
      if (task.timeLog) {
        task.timeLog.forEach(log => {
          if (isTeamView) {
            if (filterEmployee === 'all' || String(log.userId) === String(filterEmployee)) {
              logs.push({ ...log, taskTitle: task.title, taskId: task.id });
            }
          } else {
            if (String(log.userId) === String(user.id)) {
              logs.push({ ...log, taskTitle: task.title, taskId: task.id });
            }
          }
        });
      }
    });
    return logs.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const logs = getLogs(activeTab === 'team-timesheet');
  const showTeamTab = hasPermission('view_team') || hasPermission('all');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Time & Attendance</h1>
          <p className="text-gray-600 mt-1">Track work shifts and task-specific hours</p>
        </div>
      </div>

      <div className="flex space-x-1 bg-white p-1 rounded-xl border border-gray-200 shadow-sm w-fit">
        <button 
          onClick={() => setActiveTab('attendance')}
          className={`px-6 py-2 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 ${activeTab === 'attendance' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}`}
        >
          <SafeIcon icon={FiActivity} className="w-4 h-4" />
          <span>Attendance</span>
        </button>
        <button 
          onClick={() => setActiveTab('my-timesheet')}
          className={`px-6 py-2 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 ${activeTab === 'my-timesheet' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}`}
        >
          <SafeIcon icon={FiClock} className="w-4 h-4" />
          <span>Task Timer</span>
        </button>
        {showTeamTab && (
          <button 
            onClick={() => setActiveTab('team-timesheet')}
            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 ${activeTab === 'team-timesheet' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}`}
          >
            <SafeIcon icon={FiUsers} className="w-4 h-4" />
            <span>Team Logs</span>
          </button>
        )}
      </div>

      {activeTab === 'attendance' ? (
        <AttendanceTracker />
      ) : (
        <>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-2 mb-6">
              <SafeIcon icon={FiClock} className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Task Timer</h2>
            </div>
            {activeTimer ? (
              <div className="flex flex-col md:flex-row items-center justify-between bg-blue-50 p-6 rounded-xl border border-blue-100">
                <div className="mb-4 md:mb-0">
                  <p className="text-sm text-blue-600 font-medium mb-1">Currently Working On</p>
                  <h3 className="text-xl font-bold text-gray-900">{tasks.find(t => t.id === activeTimer.taskId)?.title || 'Unknown Task'}</h3>
                  <p className="text-gray-600 text-sm">{activeTimer.description || 'No description'}</p>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="text-4xl font-mono font-bold text-blue-600">{formatTime(elapsed)}</div>
                  <button onClick={stopTimer} className="flex items-center space-x-2 bg-red-500 text-white px-6 py-3 rounded-lg hover:bg-red-600 transition-colors shadow-sm">
                    <SafeIcon icon={FiSquare} className="w-4 h-4 fill-current" />
                    <span className="font-medium">Stop Timer</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <select value={selectedTask} onChange={(e) => setSelectedTask(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                  <option value="">Select a task...</option>
                  {myTasks.map(task => <option key={task.id} value={task.id}>{task.title}</option>)}
                </select>
                <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What are you working on?" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                <button onClick={handleStartTimer} disabled={!selectedTask} className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  <SafeIcon icon={FiPlay} className="w-4 h-4" />
                  <span className="font-medium">Start Timer</span>
                </button>
              </div>
            )}
          </motion.div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h3 className="font-semibold text-gray-800">{activeTab === 'team-timesheet' ? 'Team Work Logs' : 'My Work Logs'}</h3>
              <div className="flex items-center space-x-4">
                {activeTab === 'team-timesheet' && (
                  <select value={filterEmployee} onChange={(e) => setFilterEmployee(e.target.value)} className="text-sm border border-gray-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-blue-500">
                    <option value="all">All Employees</option>
                    {teamMembers.map(member => <option key={member.id} value={member.id}>{member.name}</option>)}
                  </select>
                )}
                <div className="text-sm font-medium text-gray-700 bg-gray-50 px-3 py-1 rounded-lg border border-gray-100">
                  Total: {logs.reduce((acc, log) => acc + log.hours, 0).toFixed(2)}h
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                    {activeTab === 'team-timesheet' && <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Employee</th>}
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Task</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {logs.map((log, index) => {
                    const employee = teamMembers.find(m => m.id === log.userId);
                    return (
                      <tr key={`${log.taskId}-${index}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{format(new Date(log.date), 'MMM dd, yyyy')}</td>
                        {activeTab === 'team-timesheet' && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center space-x-2">
                              <img src={employee?.avatar || 'https://via.placeholder.com/32'} alt="" className="w-6 h-6 rounded-full" />
                              <span>{employee?.name || 'Unknown'}</span>
                            </div>
                          </td>
                        )}
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{log.taskTitle}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{log.description}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-blue-600">{log.hours}h</td>
                      </tr>
                    );
                  })}
                  {logs.length === 0 && <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500">No logs found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TimeTracking;